import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, MicOff, Phone, PhoneIncoming, PhoneOff, Siren, UserRound } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

/* ---------------- calls + 911 entry ---------------- */
export function CallsApp({ demo, serverId, token, home, contacts, accent, sfx, onChanged, onSos }) {
  const call = home.call;
  return (
    <div className="p-3 flex flex-col gap-1.5">
      {call && <ActiveCall demo={demo} serverId={serverId} token={token} call={call} accent={accent} onChanged={onChanged} />}
      {!call && (
        <>
          <button data-testid="calls-911" onClick={onSos} className="w-full mb-2 py-3.5 rounded-2xl bg-red-600/90 hover:bg-red-500 font-mono text-[11px] uppercase tracking-[0.3em] font-bold text-white flex items-center justify-center gap-2 transition-colors">
            <Siren className="w-4 h-4" /> Emergency — 911
          </button>
          <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 px-1 pb-1">Citizens on this server</p>
          {contacts.length === 0 && <p className="font-mono text-[10px] text-white/40 text-center mt-8">No other citizens to call yet.</p>}
          {contacts.map((c) => (
            <div key={c.playercode} data-testid={`call-row-${c.playercode}`} className="flex items-center gap-3 px-3 py-3 rounded-xl border border-transparent hover:border-white/10 hover:bg-white/5 transition-colors">
              <span className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center"><UserRound className="w-5 h-5" style={{ color: accent }} /></span>
              <span className="flex-1"><span className="font-sans font-semibold text-sm text-white block">{c.name}</span><span className="font-mono text-[8px] text-white/40 uppercase">#{c.playercode}</span></span>
              <button data-testid={`call-btn-${c.playercode}`} onClick={async () => {
                if (demo) { toast.info("Preview: in-game this rings their phone — voice connects peer-to-peer"); return; }
                try { const r = await api.post(`/nc/servers/${serverId}/phone/calls`, { to_pc: c.playercode }, { params: { token } }); toast.success(r.data.message); onChanged(); }
                catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
              }} className="w-10 h-10 rounded-full flex items-center justify-center text-black hover:scale-105 transition-transform" style={{ backgroundColor: "#22c55e" }}>
                <Phone className="w-4 h-4" />
              </button>
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function ActiveCall({ demo, serverId, token, call: initial, accent, onChanged }) {
  const [call, setCall] = useState(initial);
  const [muted, setMuted] = useState(false);
  const [voiceNote, setVoiceNote] = useState("");
  const [secs, setSecs] = useState(0);
  const pcRef = useRef(null);
  const streamRef = useRef(null);
  const appliedRef = useRef({ offer: false, answer: false, ice: 0 });
  const remoteRef = useRef(null);
  const isCaller = initial.you_are === "caller";

  useEffect(() => {
    if (call.status !== "active") return;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [call.status]);

  const hangUp = useCallback(async () => {
    try { pcRef.current?.close(); } catch {}
    streamRef.current?.getTracks().forEach((t) => t.stop());
    if (!demo) { try { await api.post(`/nc/servers/${serverId}/phone/calls/${initial.id}/end`, {}, { params: { token } }); } catch {} }
    onChanged();
  }, [demo, serverId, initial.id, token, onChanged]);

  const setupPeer = useCallback(async (callId) => {
    try {
      streamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setVoiceNote("Mic unavailable in this environment — call is connected without voice");
    }
    const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
    pcRef.current = pc;
    streamRef.current?.getTracks().forEach((t) => pc.addTrack(t, streamRef.current));
    pc.ontrack = (e) => { if (remoteRef.current) remoteRef.current.srcObject = e.streams[0]; };
    pc.onicecandidate = (e) => {
      if (e.candidate) api.post(`/nc/servers/${serverId}/phone/calls/${callId}/signal`, { kind: "ice", data: JSON.stringify(e.candidate) }, { params: { token } }).catch(() => {});
    };
    if (isCaller) {
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      api.post(`/nc/servers/${serverId}/phone/calls/${callId}/signal`, { kind: "offer", data: JSON.stringify(offer) }, { params: { token } }).catch(() => {});
    }
  }, [serverId, token, isCaller]);

  useEffect(() => {
    if (demo) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      try {
        const r = await api.get(`/nc/servers/${serverId}/phone/calls/${initial.id}`, { params: { token } });
        const c = r.data.call;
        setCall(c);
        if (["ended", "declined", "missed"].includes(c.status)) {
          try { pcRef.current?.close(); } catch {}
          streamRef.current?.getTracks().forEach((t) => t.stop());
          return;
        }
        const pc = pcRef.current;
        if (c.status === "active" && !pc) { await setupPeer(c.id); return; }
        if (!pc) return;
        if (!isCaller && c.offer && !appliedRef.current.offer) {
          appliedRef.current.offer = true;
          await pc.setRemoteDescription(JSON.parse(c.offer));
          const ans = await pc.createAnswer();
          await pc.setLocalDescription(ans);
          await api.post(`/nc/servers/${serverId}/phone/calls/${c.id}/signal`, { kind: "answer", data: JSON.stringify(ans) }, { params: { token } });
        }
        if (isCaller && c.answer && !appliedRef.current.answer) {
          appliedRef.current.answer = true;
          await pc.setRemoteDescription(JSON.parse(c.answer));
        }
        const ice = c.ice || [];
        for (const raw of ice.slice(appliedRef.current.ice)) {
          try { await pc.addIceCandidate(JSON.parse(raw)); } catch {}
        }
        if (ice.length) appliedRef.current.ice = ice.length;
      } catch {}
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => { stopped = true; clearInterval(t); };
  }, [demo, serverId, initial.id, token, isCaller, setupPeer]);

  const toggleMute = () => {
    streamRef.current?.getAudioTracks().forEach((t) => (t.enabled = muted));
    setMuted(!muted);
  };

  const other = isCaller ? initial.callee_name : initial.caller_name;
  const fmt = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;

  return (
    <div data-testid="active-call" className="rounded-2xl border border-white/10 bg-white/5 p-5 flex flex-col items-center gap-3 mb-2">
      <span className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center"><UserRound className="w-8 h-8" style={{ color: accent }} /></span>
      <p className="font-sans font-bold text-lg text-white">{other || `#${isCaller ? initial.callee_pc : initial.caller_pc}`}</p>
      <p data-testid="call-status" className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
        {call.status === "ringing" ? (isCaller ? "Ringing…" : "Connecting…") : call.status === "active" ? fmt : call.status}
      </p>
      {voiceNote && <p className="font-mono text-[9px] text-amber-300 text-center">{voiceNote}</p>}
      <div className="flex gap-4 mt-1">
        <button data-testid="call-mute" onClick={toggleMute} className={`w-12 h-12 rounded-full flex items-center justify-center border transition-colors ${muted ? "bg-white text-black border-white" : "border-white/25 text-white hover:bg-white/10"}`}>
          {muted ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
        </button>
        <button data-testid="call-end" onClick={hangUp} className="w-12 h-12 rounded-full bg-red-500 hover:bg-red-400 flex items-center justify-center text-white transition-colors"><PhoneOff className="w-5 h-5" /></button>
      </div>
      <audio ref={remoteRef} autoPlay className="hidden" />
    </div>
  );
}

export function IncomingCall({ call, accent, demo, serverId, token, onChanged }) {
  const act = async (action) => {
    if (demo) { toast.info(action === "accept" ? "Preview: answering connects voice peer-to-peer" : "Preview: declined"); return; }
    try { await api.post(`/nc/servers/${serverId}/phone/calls/${call.id}/${action}`, {}, { params: { token } }); onChanged(); if (action === "accept") toast.success("Connected — open Calls to talk"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div data-testid="incoming-call" className="absolute inset-0 z-20 bg-black/85 backdrop-blur-md flex flex-col items-center justify-center gap-4" style={{ animation: "ncFadeIn 0.25s ease both" }}>
      <span className="w-20 h-20 rounded-full bg-white/10 flex items-center justify-center animate-pulse"><PhoneIncoming className="w-9 h-9" style={{ color: accent }} /></span>
      <p className="font-sans font-bold text-2xl text-white">{call.caller_name || `#${call.caller_pc}`}</p>
      <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/60">incoming call</p>
      <div className="flex gap-8 mt-4">
        <button data-testid="call-decline" onClick={() => act("decline")} className="w-16 h-16 rounded-full bg-red-500 hover:bg-red-400 flex flex-col items-center justify-center text-white transition-colors"><PhoneOff className="w-6 h-6" /></button>
        <button data-testid="call-accept" onClick={() => act("accept")} className="w-16 h-16 rounded-full bg-emerald-500 hover:bg-emerald-400 flex items-center justify-center text-white transition-colors animate-bounce"><Phone className="w-6 h-6" /></button>
      </div>
    </div>
  );
}

